/**
 * Custom login domain — dash data-layer client (FS-676).
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/custom-login-domain` surface. Mirrors the contract in
 * `apps/api/src/routes/custom-login-domain.ts`:
 *
 *   GET    /                 -> { domains: CustomLoginDomainView[] }
 *   POST   /                 { domain }
 *                             -> 201 { domain }
 *                             400 invalid_domain
 *                             409 already_claimed
 *   DELETE /:id               -> { deleted: true, id }
 *   POST   /:id/verify        -> { domain }
 *
 * Pure helpers (`isValidFqdn`, `normalizeDomain`, `isReservedDomain`,
 * `domainDraftSchema`) are kept in this module so both the page and the
 * unit tests can import them without pulling React.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Row shapes — match server `CustomLoginDomainView`.
// ---------------------------------------------------------------------------

export interface CustomLoginDomainView {
	id: string;
	domain: string;
	verified: boolean;
	active: boolean;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface ListDomainsResponse {
	domains: CustomLoginDomainView[];
}

export interface CreateDomainResponse {
	domain: CustomLoginDomainView;
}

export interface VerifyDomainResponse {
	domain: CustomLoginDomainView;
}

export interface DeleteDomainResponse {
	deleted: true;
	id: string;
}

// ---------------------------------------------------------------------------
// FQDN validation — kept verbatim with the server regex so tests catch drift.
// ---------------------------------------------------------------------------

const FQDN_RE =
	/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** The zone operators may not claim sub-hostnames of. */
export const RESERVED_ROOT = "trackerity.com";

export function normalizeDomain(input: string): string {
	return (input ?? "").trim().toLowerCase().replace(/\.$/, "");
}

export function isReservedDomain(domain: string): boolean {
	const d = normalizeDomain(domain);
	if (d.length === 0) return false;
	return d === RESERVED_ROOT || d.endsWith(`.${RESERVED_ROOT}`);
}

export function isValidFqdn(domain: string): boolean {
	if (typeof domain !== "string") return false;
	const d = domain.trim();
	if (d.length === 0 || d.length > 253) return false;
	if (d.includes(" ") || d.includes("/") || d.includes(":")) return false;
	if (d.startsWith(".") || d.startsWith("-")) return false;
	return FQDN_RE.test(d.toLowerCase().replace(/\.$/, ""));
}

// ---------------------------------------------------------------------------
// Add-domain form draft + hand-rolled safeParse surface.
// ---------------------------------------------------------------------------

export interface DomainDraft {
	domain: string;
}

export const EMPTY_DRAFT: DomainDraft = { domain: "" };

export interface ValidatedDomainDraft {
	domain: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const domainDraftSchema = {
	safeParse(input: DomainDraft): ParseResult<ValidatedDomainDraft> {
		const errors: Record<string, string> = {};
		const normalized = normalizeDomain(input.domain);
		if (normalized.length === 0) {
			errors.domain = "Domain is required.";
		} else if (!isValidFqdn(normalized)) {
			errors.domain =
				"Enter a fully-qualified domain name (e.g. partners.brand.com).";
		} else if (isReservedDomain(normalized)) {
			errors.domain =
				"Trackerity.com sub-domains are reserved — use your own zone.";
		}
		if (Object.keys(errors).length > 0) return { success: false, errors };
		return { success: true, data: { domain: normalized } };
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function verifiedBadgeClass(verified: boolean): string {
	return verified
		? "bg-emerald-50 text-emerald-700 border-emerald-200"
		: "bg-amber-50 text-amber-700 border-amber-200";
}

export function verifiedLabel(verified: boolean): string {
	return verified ? "Verified" : "Unverified";
}

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
		return "—";
	}
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Sort newest-first, grouping unverified before verified (action needed). */
export function sortDomains(
	domains: readonly CustomLoginDomainView[],
): CustomLoginDomainView[] {
	return domains
		.slice()
		.sort((a, b) => {
			if (a.verified !== b.verified) return a.verified ? 1 : -1;
			return b.created_at - a.created_at;
		});
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/custom-login-domain";

export async function listDomains(): Promise<CustomLoginDomainView[]> {
	try {
		const res = await apiClient.get<ListDomainsResponse>(BASE);
		return res.domains ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export type CreateDomainOutcome =
	| { kind: "ok"; domain: CustomLoginDomainView }
	| {
			kind: "already_claimed";
			by_current_tenant: boolean;
	  }
	| { kind: "invalid"; message: string };

export async function createDomain(
	payload: ValidatedDomainDraft,
): Promise<CreateDomainOutcome> {
	try {
		const res = await apiClient.post<CreateDomainResponse>(BASE, {
			domain: payload.domain,
		});
		return { kind: "ok", domain: res.domain };
	} catch (err) {
		if (err instanceof ApiError) {
			const body = err.body as
				| { error?: string; by_current_tenant?: boolean; issues?: unknown }
				| null;
			if (err.status === 409 && body?.error === "already_claimed") {
				return {
					kind: "already_claimed",
					by_current_tenant: Boolean(body.by_current_tenant),
				};
			}
			if (err.status === 400) {
				return {
					kind: "invalid",
					message: "Domain rejected by server. Check the format and try again.",
				};
			}
		}
		throw err;
	}
}

export async function deleteDomain(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function verifyDomain(
	id: string,
): Promise<CustomLoginDomainView | null> {
	try {
		const res = await apiClient.post<VerifyDomainResponse>(
			`${BASE}/${encodeURIComponent(id)}/verify`,
		);
		return res.domain;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

// ---------------------------------------------------------------------------
// TanStack Query keys + cache merge helpers
// ---------------------------------------------------------------------------

export const customLoginDomainQueryKeys = {
	all: ["custom-login-domain"] as const,
	list: () => [...customLoginDomainQueryKeys.all, "list"] as const,
};

export function replaceDomain(
	domains: readonly CustomLoginDomainView[],
	next: CustomLoginDomainView,
): CustomLoginDomainView[] {
	return domains.map((d) => (d.id === next.id ? next : d));
}

export function removeDomain(
	domains: readonly CustomLoginDomainView[],
	id: string,
): CustomLoginDomainView[] {
	return domains.filter((d) => d.id !== id);
}

export function prependDomain(
	domains: readonly CustomLoginDomainView[],
	next: CustomLoginDomainView,
): CustomLoginDomainView[] {
	return [next, ...domains];
}
